import {useState, useEffect} from 'react';
import {EDITOR_MODE} from '@/Constant';
import sdk from '@/sdk';
import * as store from '@/store';
import eventBus, {DOCUMENT_INIT_COMPLETE, DOCUMENT_VIEW_CLICK_CHANGE} from '@/eventBus';
import {getNodeById} from '@/domains/space/Repository/SidebarNew/sidebarTreeUtils';
import {isPrivateAndNoPower} from '@/domains/space/Repository/knowledgeUtils';
import {pullCardStorageObj, tipStorageObj} from '@/utils/document/common';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {IMeetingInfo} from '@/utils/document/meetingMode';

import UnPublishNotification from './unPublishNotification';
import PullCardTip from './pullCardTip';
import EmptyParentNodeGuidance, {InsertEmbed} from './emptyParentNodeTip';
import {isCurDocPageEditable} from '@/store/util';
import {getMorphoService, serviceNames} from '@/rte/editor';
import ExternalUserTip from './ExternalUserTip';
import MeetingTip from './MeetingTip';
import {VIEW_BLOCK} from '@/hooks/queryViewController/config';
import {isShowCurrentView} from '@/hooks/queryViewController';

const {
    useIsInGray: useIsOpenSubDocList,
} = initGray(GRAY_RELEASE_KEYS.subDocList);

interface IProps {
    mode: string;
    showUnPublishTip: boolean;
    onShowUnPublishTipChange: (params: boolean) => void;
    onSwitchEdit: () => void;
    isKuShareMode?: string;
    showMeetingTip: boolean;
    meetingInfo?: IMeetingInfo;
}

function EditorTopTip(props: IProps) {
    const {
        mode,
        showUnPublishTip,
        onSwitchEdit,
        onShowUnPublishTipChange,
        isKuShareMode,
        showMeetingTip,
        meetingInfo,
    } = props;
    const editable = isCurDocPageEditable();
    const isShowPublishTip = showUnPublishTip && mode === EDITOR_MODE.preview;
    const isShowToMode = mode === EDITOR_MODE.preview || mode === EDITOR_MODE.edit;


    const [isShowEmptyParentNodeTip, setShowEmptyParentNodeTip] = useState(false);
    const [isInsertPreview, setIsInsertPreview] = useState(false); // 存储点击预览态的一键插入 在文档加载完成时是否要执行插入操作
    const [isPullCardVis, setIsPullCardVis] = useState(false); // 是否显示拆卡推荐的引导
    const [isTocUpdateVis, setIsTocUpdateVis] = useState(false); // 是否显示文档更新的引导
    const isOpenSubDocList = useIsOpenSubDocList();

    /**
     * 拆卡引导出现逻辑：
     * 1.编辑态且可编辑
     * 2.文档中有3个及以上最高级标题
     * 3.文档字数大于1000字
     * 4.文档被访量大于2
     * 5.文档视图下仅包含一张卡片
     */
    const isShowPullCardTip = async () => {
        if (window.isTocPage) {
            return;
        }
        const editorStateService = getMorphoService(serviceNames.IEditorStateService);
        if (editorStateService) {
            const editor = editorStateService?.editor;
            const titleNode = editor.children[0];
            let highestLevel = 6;
            let topLevelHeadings = [];

            // 被访量
            const curDocument = store.getCurDocument();
            let pageView = 0;
            if (curDocument?.repositoryGuid) {
                try {
                    const ksServer = await sdk.getKsServer();
                    const result = await ksServer.getRecentBrowseRecordsAndUv({
                        docGuid: curDocument?.docGuid,
                        repositoryGuid: curDocument.repositoryGuid,
                    });
                    pageView = result?.uv || 0;
                } catch (error) {
                    console.log(error);
                }
            }

            // 判断视图以及卡片视图下是否仅包含一张卡片。
            const isOnlyOneCard = () => {
                if (titleNode.slide === true) {
                    // 卡片视图
                    if (editor.children.length === 2) {
                        // 仅有一个卡片结点
                        return true;
                    }
                    return false;
                }
                // 普通视图
                return true;
            };
            const onlyOneCard = isOnlyOneCard();

            const editorRef = store.getEditorRef().current!;
            // 文档字数大于1000字
            const docLength = editorRef.getDocLength({
                type: 'word',
            });

            // 文档最高级标题数量
            // eslint-disable-next-line max-len
            const hasCardNode = editor.children.some((child: any) => child?.type === 'card' || child?.type === 'transclusion-reference');
            if (hasCardNode) {
                const cardsChildren: any = [];
                editor.children.forEach((node: any) => {
                    if (node.type === 'card' && node.children[0]?.children) {
                        cardsChildren.push(...node.children[0].children);
                    }
                });
                topLevelHeadings = cardsChildren.filter((node: any) => {
                    if (node.type === 'heading') {
                        if (node.level < highestLevel) {
                            highestLevel = node.level;
                        }
                    }
                    return node.type === 'heading';
                });
            } else {
                topLevelHeadings = editor.children.filter((node: any) => {
                    if (node.type === 'heading') {
                        if (node.level < highestLevel) {
                            highestLevel = node.level;
                        }
                    }
                    return node.type === 'heading';
                });
            }
            const highestLevelHeadings = topLevelHeadings.filter((node: any) => node.level === highestLevel);

            // eslint-disable-next-line max-len
            if (!pullCardStorageObj.isCacheClose() && highestLevelHeadings.length >= 3 && docLength > 1000 && pageView > 2 && onlyOneCard) {
                setIsPullCardVis(true);
            } else {
                setIsPullCardVis(false);
            }
        }
    };

    useEffect(() => {
        const docViewClickChange = () => {
            if (editable && mode === EDITOR_MODE.edit) {
                setTimeout(() => {
                    isShowPullCardTip();
                }, 0);
            }
        };

        eventBus.on(DOCUMENT_VIEW_CLICK_CHANGE, docViewClickChange);

        if (editable && mode === EDITOR_MODE.edit) {
            setIsTocUpdateVis(true);
        } else {
            setIsTocUpdateVis(false);
        }
        return () => {
            eventBus.off(DOCUMENT_VIEW_CLICK_CHANGE, docViewClickChange);
            setIsPullCardVis(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);


    useEffect(() => {
        // 文档渲染完成后
        // (编辑态+预览态) + 文档为空 + 未缓存关闭提示 + 存在子文档 -> 显示子文档列表卡插入引导 + 有编辑权限
        function onDocInitComplete() {
            if (window.isTocPage) {
                return;
            }
            const cacheClose = tipStorageObj.isCacheClose();
            const editorRef = store.getEditorRef();
            const isEmpty = !!editorRef.current?.isEmptyDocument();
            const curDocument = store.getCurDocument();

            if (editable && mode === EDITOR_MODE.edit) {
                isShowPullCardTip();
            }

            // 预览态点击了插入 只有在编辑态时，执行子文档列表卡的插入操作
            if (isInsertPreview && mode === EDITOR_MODE.edit) {
                InsertEmbed(EDITOR_MODE.preview);
                setIsInsertPreview(false);
                setShowEmptyParentNodeTip(false);
                return;
            }

            if (cacheClose || !isEmpty || !curDocument || !editable || !isShowToMode || isKuShareMode) {
                setShowEmptyParentNodeTip(false);
                return;
            }

            const treeData = store.getSidebarTreeObj()?.value || [];
            const curNode = getNodeById(treeData, curDocument.docGuid);
            if (treeData.length === 0 || !curNode) {
                const privateNoPower = isPrivateAndNoPower();

                if (privateNoPower) {
                    sdk.as.getPermissionDirList(curDocument.repositoryGuid, curDocument.docGuid).then((res) => {
                        if (res && res.length > 0) {
                            setShowEmptyParentNodeTip(true);
                        }
                    });
                } else {
                    sdk.getKsServer().then(ksServer => {
                        ksServer.getDocumentsNew({
                            parentGuid: curDocument.docGuid,
                        }).then((res) => {
                            if (res && res.length > 0) {
                                setShowEmptyParentNodeTip(true);
                            }
                        });
                    });
                }
            } else {
                const isExistChild = !curNode?.isLeaf;
                setShowEmptyParentNodeTip(isExistChild);
            }
        }

        eventBus.on(DOCUMENT_INIT_COMPLETE, onDocInitComplete);
        return () => {
            eventBus.off(DOCUMENT_INIT_COMPLETE, onDocInitComplete);
            setShowEmptyParentNodeTip(false);
            setIsPullCardVis(false);
        };
    // eslint-disable-next-line max-len
    }, [mode, editable, isShowToMode, isInsertPreview, isKuShareMode]);

    // 按优先级展示编辑区域上方的提示
    const renderComp = () => {
        if (window.isTocPage && isTocUpdateVis) {
            return <ExternalUserTip
                setIsTocUpdateVis={setIsTocUpdateVis}
            />;
        }

        if (showMeetingTip && meetingInfo) {
            return <MeetingTip meetingInfo={meetingInfo} />;
        }

        if (!window.isTocPage && isPullCardVis) {
            return <PullCardTip
                setIsPullCardVis={setIsPullCardVis}
            />;
        }

        if (isShowPublishTip && !(window as any).isSdkPage && isShowCurrentView(VIEW_BLOCK.editorPublishTip)) {
            return (
                <UnPublishNotification
                    onShowUnPublishTipChange={onShowUnPublishTipChange}
                    onSwitchEdit={onSwitchEdit}
                />
            );
        }
        const repository = store.getCurRepository();
        // eslint-disable-next-line max-len
        if (!window.isTocPage && !repository?.external && isShowEmptyParentNodeTip && ((mode === EDITOR_MODE.preview) || (mode === EDITOR_MODE.edit && isOpenSubDocList))) {
            return <EmptyParentNodeGuidance
                onClose={() => setShowEmptyParentNodeTip(false)}
                mode={mode}
                setIsInsertPreview={setIsInsertPreview}
                onSwitchEdit={onSwitchEdit}
            />;
        }

        return null;
    };

    return renderComp();
}

export default EditorTopTip;
